/**
 * CONFIGURAÇÕES DO INSPECT IA REVIEW — versionadas, editadas em
 * INSPECT IA REVIEW → CONFIGURAÇÕES IA (ai_settings.read / ai_settings.update).
 *
 * Segredo de provedor (API key) NUNCA mora aqui: não é aceito do navegador,
 * não vai para log nem para o banco em texto aberto. Nenhum provedor externo
 * está integrado nesta entrega — o campo existe e diz isso.
 */
export type AIProviderId = "INSPECT_IA_LOCAL" | "EXTERNAL_HTTP";

export const AI_PROVIDER_LABELS: Readonly<Record<AIProviderId, string>> = {
  INSPECT_IA_LOCAL: "INSPECT IA — motor local de regras (determinístico)",
  EXTERNAL_HTTP: "Provedor externo (LLM) — NÃO INTEGRADO"
};

export interface AISettings {
  readonly version: number;
  readonly provider: AIProviderId;
  readonly model: string;
  readonly endpoint: string;
  readonly maxTokens: number;
  readonly timeoutMs: number;
  readonly retries: number;
  readonly temperature: number;
  readonly knowledgeBaseEnabled: boolean;
  /** Aplicar correções linguísticas SEGURAS automaticamente ao executar a revisão. */
  readonly autoApplySafeLanguage: boolean;
  readonly requireReviewBeforeSubmit: boolean;
  readonly offlinePolicy: "BLOCK" | "ALLOW_WITH_RECORD";
  /** Log só de metadados (quem, quando, contagens) — conteúdo do relatório nunca vai para log. */
  readonly logging: "METADATA_ONLY" | "OFF";
  readonly updatedAt: string;
  readonly updatedBy: string;
}

export const DEFAULT_AI_SETTINGS: AISettings = {
  version: 0,
  provider: "INSPECT_IA_LOCAL",
  model: "regras-ptbr",
  endpoint: "",
  maxTokens: 4000,
  timeoutMs: 20000,
  retries: 1,
  temperature: 0,
  knowledgeBaseEnabled: true,
  autoApplySafeLanguage: true,
  requireReviewBeforeSubmit: false,
  offlinePolicy: "ALLOW_WITH_RECORD",
  logging: "METADATA_ONLY",
  updatedAt: "",
  updatedBy: ""
};

export function normalizeAISettings(raw: Partial<AISettings> | null | undefined): AISettings {
  const r = raw ?? {};
  const num = (v: unknown, min: number, max: number, def: number) => (typeof v === "number" && Number.isFinite(v) ? Math.min(max, Math.max(min, v)) : def);
  return {
    version: typeof r.version === "number" ? r.version : 0,
    provider: r.provider === "EXTERNAL_HTTP" ? "EXTERNAL_HTTP" : "INSPECT_IA_LOCAL",
    model: typeof r.model === "string" && r.model.trim() ? r.model.trim() : DEFAULT_AI_SETTINGS.model,
    endpoint: typeof r.endpoint === "string" ? r.endpoint.trim() : "",
    maxTokens: num(r.maxTokens, 256, 200000, DEFAULT_AI_SETTINGS.maxTokens),
    timeoutMs: num(r.timeoutMs, 1000, 120000, DEFAULT_AI_SETTINGS.timeoutMs),
    retries: Math.round(num(r.retries, 0, 3, DEFAULT_AI_SETTINGS.retries)),
    temperature: num(r.temperature, 0, 1, DEFAULT_AI_SETTINGS.temperature),
    knowledgeBaseEnabled: r.knowledgeBaseEnabled !== false,
    autoApplySafeLanguage: r.autoApplySafeLanguage !== false,
    // A IA é 100% consultiva e não bloqueante por definição — neutraliza configurações legadas
    requireReviewBeforeSubmit: false,
    offlinePolicy: "ALLOW_WITH_RECORD",
    logging: r.logging === "OFF" ? "OFF" : "METADATA_ONLY",
    updatedAt: typeof r.updatedAt === "string" ? r.updatedAt : "",
    updatedBy: typeof r.updatedBy === "string" ? r.updatedBy : ""
  };
}

/** Recusa campos que NUNCA entram nas configurações (segredos). */
export function assertNoSecrets(raw: Record<string, unknown>): void {
  for (const key of Object.keys(raw)) {
    if (/key|secret|token|password|senha|credential/i.test(key)) {
      throw new Error("Credenciais de provedor não são aceitas aqui: ficam só no cofre do servidor.");
    }
  }
}
